import { clientEnvModule } from "@/modules/client-env";

export type UtmParams = Record<string, string>;

/**
 * Stores captured UTM params for the session.
 * These are captured early (before React mounts) and stored here
 * so analytics can access them even after we strip them from the URL.
 */
let capturedUtmParams: UtmParams | null = null;

/**
 * Checks if a key is a UTM parameter (starts with "utm_").
 */
const isUtmKey = (key: string): boolean => key.startsWith("utm_");

/**
 * Extracts all UTM parameters (any key starting with "utm_") from a URLSearchParams object.
 */
const extractUtmParams = (searchParams: URLSearchParams): UtmParams => {
  const params: UtmParams = {};

  for (const [key, value] of searchParams.entries()) {
    if (isUtmKey(key) && value) {
      params[key] = value;
    }
  }

  return params;
};

/**
 * Checks if there are any UTM params in the given search params.
 */
const hasUtmParams = (searchParams: URLSearchParams): boolean => {
  for (const [key] of searchParams.entries()) {
    if (isUtmKey(key)) {
      return true;
    }
  }
  return false;
};

/**
 * Creates a new search string with all UTM params (any key starting with "utm_") removed.
 */
const stripUtmFromSearch = (searchParams: URLSearchParams): string => {
  const newParams = new URLSearchParams(searchParams);

  // Strip any param that starts with "utm_"
  for (const [key] of Array.from(newParams.entries())) {
    if (isUtmKey(key)) {
      newParams.delete(key);
    }
  }

  const result = newParams.toString();
  return result ? `?${result}` : "";
};

/**
 * Captures UTM parameters from the current URL, stores them for analytics,
 * and strips them from the URL so the router doesn't have to handle them.
 *
 * Should be called early in entry.ts, before React Router initializes.
 *
 * This ensures:
 * 1. UTM params are captured for analytics tracking
 * 2. The URL is cleaned up for the router
 * 3. Users don't see ugly UTM params in their URL bar
 */
export const captureAndStripUtmParams = (): void => {
  const isDesktop = clientEnvModule.isDesktop();
  const currentUrl = new URL(document.URL);

  let searchParams: URLSearchParams;
  let currentPath: string;

  if (isDesktop) {
    // HashRouter - params are in the hash
    const hash = currentUrl.hash || "#/";
    const hashParts = hash.split("?");
    currentPath = hashParts[0] || "#/";
    searchParams = new URLSearchParams(hashParts[1] || "");
  } else {
    // BrowserRouter - params are in search
    currentPath = currentUrl.pathname;
    searchParams = new URLSearchParams(currentUrl.search);
  }

  // Check if there are any UTM params to process
  if (!hasUtmParams(searchParams)) {
    return;
  }

  // Capture the UTM params for analytics
  capturedUtmParams = extractUtmParams(searchParams);

  // Also persist to sessionStorage so they survive page refreshes during login flow
  try {
    sessionStorage.setItem("mem_utm_params", JSON.stringify(capturedUtmParams));
  } catch {
    // Silently fail - sessionStorage might not be available
  }

  // Strip UTM params from the URL
  const newSearch = stripUtmFromSearch(searchParams);
  let newUrl: string;

  if (isDesktop) {
    // For desktop HashRouter, ensure we properly reconstruct the hash URL
    // newSearch already includes "?" prefix if there are params, or is empty string
    newUrl = currentPath + (newSearch ? newSearch : "");
  } else {
    // For browser router
    newUrl = currentPath + newSearch;
  }

  window.history.replaceState(null, "", newUrl);
};

/**
 * Gets the captured UTM parameters.
 * Returns null if no UTM params were captured.
 *
 * First checks in-memory storage, then falls back to sessionStorage
 * (for cases where the page was refreshed during login flow).
 */
export const getCapturedUtmParams = (): UtmParams | null => {
  if (capturedUtmParams) {
    return capturedUtmParams;
  }

  // Try to recover from sessionStorage
  try {
    const stored = sessionStorage.getItem("mem_utm_params");
    if (stored) {
      capturedUtmParams = JSON.parse(stored) as UtmParams;
      return capturedUtmParams;
    }
  } catch {
    // Silently fail
  }

  return null;
};

/**
 * Clears the captured UTM parameters.
 * Generally not needed - UTM params persist for the session.
 */
export const clearCapturedUtmParams = (): void => {
  capturedUtmParams = null;

  try {
    sessionStorage.removeItem("mem_utm_params");
  } catch {
    // Silently fail
  }
};

/**
 * Checks if there are any captured UTM params available.
 */
export const hasCapturedUtmParams = (): boolean => {
  return getCapturedUtmParams() !== null;
};
